import '@backstage/cli/asset-types';
import ReactDOM from 'react-dom/client';
import App from './App';
import '@backstage/ui/css/styles.css';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

// Read the per-request nonce injected by the Lambda@Edge CspNonceFunction.
// Passed to Emotion so its <style> tags carry the nonce attribute.
// Note: the CSP style-src directive intentionally does NOT include this nonce (it stays
// 'unsafe-inline' only) — per the CSP spec, a nonce-source present in a directive causes
// browsers to ignore 'unsafe-inline' in that SAME directive entirely, even for elements
// without a matching nonce. JSS (MUI v4), swagger-editor, and Tealeaf still inject
// <style> elements with no nonce at all, so adding one to style-src would silently block
// their styles. See infra/lambdas/csp_nonce/index.js for the full explanation.
// window.litNonce/window.__webpack_nonce__ below are still set proactively — PDS
// (Lit-based) components read window.litNonce per
// https://design.principal.com/4eed865c5/p/600024-developing-with-pds/b/45acec — so PDS
// is ready for whenever style-src eventually switches to nonce-only.
const cspNonce =
  document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content ??
  '';

// JSS (used by MUI v4 components) checks window.__webpack_nonce__ each time it creates
// a <style> element. Setting this before React renders stamps every JSS stylesheet
// with the nonce attribute as defence-in-depth. The current CSP keeps style-src as
// 'unsafe-inline' (no nonce-source), so this does not affect current runtime behaviour,
// but it ensures JSS stylesheets are nonce-ready if style-src ever switches to
// nonce-only in the future.
// window.litNonce is the PDS/Lit equivalent: Lit reads it for any fallback shadow-DOM
// <style> elements (browsers without constructable stylesheet support), and PDS
// components that inject light-DOM <style> elements into <head> read it too. Must be
// set before PDS custom elements are instantiated, i.e. before React renders them.
if (cspNonce) {
  (window as any).__webpack_nonce__ = cspNonce;
  (window as any).litNonce = cspNonce;
}

const emotionCache = createCache({ key: 'css', nonce: cspNonce });

ReactDOM.createRoot(document.getElementById('root')!).render(
  <CacheProvider value={emotionCache}>
    <App />
  </CacheProvider>,
);
